import { useMemo, useState } from 'react';

import { EocError, isNetworkError, useReportError } from '@atlassian/eoc-global-context';

import { type DefaultCatch, type DefaultCatchConfig } from './types';

export function useDefaultCatch(): DefaultCatch {
	const reportError = useReportError();
	const [error, setError] = useState<EocError>();

	if (error) {
		throw error;
	}

	return useMemo(() => {
		const catchHandler = <R>(
			{
				id,
				httpErrorHandler,
				reporter: customReporter,
				attributes,
				teamName,
			}: DefaultCatchConfig<R>,
			error: unknown,
		): R | void => {
			if (!isNetworkError(error)) {
				return setError(new EocError(id, error));
			}

			if (customReporter !== null) {
				const reporter = customReporter ?? reportError;
				reporter(id, error, attributes, teamName);
			}

			try {
				return httpErrorHandler?.(error);
			} catch (handlerError) {
				if (!isNetworkError(handlerError)) {
					setError(new EocError(id, handlerError));
				}

				throw handlerError;
			}
		};

		return (<R>(config: DefaultCatchConfig<R>, error?: unknown) => {
			if (error !== undefined) {
				return catchHandler(config, error);
			}
			return (err: unknown) => catchHandler(config, err);
		}) as DefaultCatch;
	}, [reportError]);
}
